/* ==========================================================================
   Stories Gallery — vanilla JS player engine
   - Guarded so it only initializes once (multiple sections / theme-editor re-renders)
   - Event delegation on [data-stg-open] so re-rendered sections keep working
   - Pointer events only (works on desktop + mobile)
   - On desktop the stories either side of the current one preview beside the stage;
     callers can opt the playlist into looping
   ========================================================================== */
(function () {
  'use strict';

  if (window.__stgReady) return;
  window.__stgReady = true;

  var HOLD_MS = 180; // press-and-hold threshold to pause
  var SWIPE_PX = 90; // swipe-down distance to close
  var SWIPE_X_PX = 60; // swipe-sideways distance to change story

  // ---- State ----
  var stories = []; // current playlist
  var index = 0;
  var rafId = null;
  var startTs = 0; // performance timestamp when current segment (re)started
  var elapsed = 0; // ms accumulated for current story before a pause
  var paused = false;
  var holdTimer = null;
  var pointerStart = null; // {x, y, t}
  var moved = false;
  var activeLayer = 0; // which media layer is currently visible (0/1)
  var preloadImg = new Image();
  var muted = false; // sound on by default; user can toggle via header button
  var currentUid = ''; // uid of the instance being played, for per-story overlay lookups
  var looping = false; // opt-in per playlist: wrap around instead of closing at the end

  // ---- Build viewer DOM once ----
  var viewer, frame, stage, layers, progressEl, avatarEl, titleEl, subtitleEl, ctaEl, segFills, muteBtn, quickAddEl;
  var peekPrev, peekNext; // desktop-only previews of the neighbouring stories

  // Inline SVG icons for the sound toggle.
  var ICON_SOUND_ON =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
  var ICON_SOUND_OFF =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';

  function updateMuteBtn() {
    if (!muteBtn) return;
    muteBtn.innerHTML = muted ? ICON_SOUND_OFF : ICON_SOUND_ON;
    muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
  }

  function toggleMute() {
    muted = !muted;
    var v = layers[activeLayer].media;
    if (v && layers[activeLayer].type === 'video') {
      v.muted = muted;
      // If unmuting, make sure playback is actually running.
      if (!muted && !paused) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
    }
    updateMuteBtn();
  }

  function buildViewer() {
    viewer = document.createElement('div');
    viewer.className = 'stg-viewer';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Stories');

    stage = document.createElement('div');
    stage.className = 'stg-stage';

    // Two media layers for cross-fade. Each can hold an <img> or <video>.
    layers = [makeLayer(), makeLayer()];
    stage.appendChild(layers[0].el);
    stage.appendChild(layers[1].el);

    // Tap zones (left = prev, middle = noop, right = next)
    var zones = document.createElement('div');
    zones.className = 'stg-zones';
    var zPrev = document.createElement('div');
    var zMid = document.createElement('div');
    var zNext = document.createElement('div');
    zPrev.className = zMid.className = zNext.className = 'stg-zone';
    zPrev.setAttribute('data-stg-zone', 'prev');
    zMid.setAttribute('data-stg-zone', 'mid');
    zNext.setAttribute('data-stg-zone', 'next');
    zones.appendChild(zPrev);
    zones.appendChild(zMid);
    zones.appendChild(zNext);
    stage.appendChild(zones);

    // Progress bar
    progressEl = document.createElement('div');
    progressEl.className = 'stg-progress';
    stage.appendChild(progressEl);

    // Header
    var header = document.createElement('div');
    header.className = 'stg-header';
    avatarEl = document.createElement('img');
    avatarEl.className = 'stg-header__avatar';
    avatarEl.alt = '';
    var htext = document.createElement('div');
    htext.className = 'stg-header__text';
    titleEl = document.createElement('span');
    titleEl.className = 'stg-header__title';
    subtitleEl = document.createElement('span');
    subtitleEl.className = 'stg-header__subtitle';
    htext.appendChild(titleEl);
    htext.appendChild(subtitleEl);
    muteBtn = document.createElement('button');
    muteBtn.className = 'stg-mute';
    muteBtn.type = 'button';
    updateMuteBtn();
    muteBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleMute();
    });
    muteBtn.addEventListener('pointerdown', function (e) {
      e.stopPropagation();
    });
    var closeBtn = document.createElement('button');
    closeBtn.className = 'stg-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      close();
    });
    header.appendChild(avatarEl);
    header.appendChild(htext);
    header.appendChild(muteBtn);
    header.appendChild(closeBtn);
    stage.appendChild(header);

    // CTA
    ctaEl = document.createElement('a');
    ctaEl.className = 'stg-cta';
    ctaEl.target = '_top';
    ctaEl.addEventListener('pointerdown', function (e) {
      e.stopPropagation();
    });
    stage.appendChild(ctaEl);

    // Product action pinned to the bottom of the story. Its HTML is injected per
    // story from a <template> (see setOverlay). Stop pointer
    // events from reaching the tap-to-navigate zones, but let clicks bubble to the
    // theme's [data-quick-add] handler.
    quickAddEl = document.createElement('div');
    quickAddEl.className = 'stg-quickadd';
    quickAddEl.addEventListener('pointerdown', function (e) {
      e.stopPropagation();
    });
    stage.appendChild(quickAddEl);

    // On desktop the stage sits in a row between scaled-down previews of the
    // stories either side; the previews collapse to nothing on narrow screens.
    frame = document.createElement('div');
    frame.className = 'stg-frame';
    peekPrev = makePeek('prev');
    peekNext = makePeek('next');
    frame.appendChild(peekPrev.el);
    frame.appendChild(stage);
    frame.appendChild(peekNext.el);

    viewer.appendChild(frame);
    document.body.appendChild(viewer);

    // Click on the backdrop (outside the story stage) closes, like the × button.
    viewer.addEventListener('click', function (e) {
      if (e.target === viewer || e.target === frame) close();
    });

    // Pointer + keyboard interactions
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    stage.addEventListener('pointercancel', onPointerUp);
    document.addEventListener('keydown', onKeyDown);
  }

  function makeLayer() {
    // A layer container holding either an <img> or <video>, swapped per story.
    var el = document.createElement('div');
    el.className = 'stg-media';
    return { el: el, media: null, type: null };
  }

  // ---- Neighbour previews ----
  // A still frame of the story either side, dimmed and scaled back. Video stories
  // show their poster rather than a second playing <video>.
  function makePeek(dir) {
    var el = document.createElement('div');
    el.className = 'stg-peek stg-peek--' + dir;
    el.setAttribute('aria-hidden', 'true');
    var img = document.createElement('img');
    img.className = 'stg-peek__img';
    img.alt = '';
    img.decoding = 'async';
    el.appendChild(img);
    el.addEventListener('click', function (e) {
      e.stopPropagation();
      if (dir === 'prev') {
        prev();
      } else {
        next();
      }
    });
    return { el: el, img: img };
  }

  function paintPeek(peek, story) {
    var src = story && (story.img || story.poster);
    if (src) {
      peek.img.src = src;
      peek.el.classList.add('is-visible');
    } else {
      peek.img.removeAttribute('src');
      peek.el.classList.remove('is-visible');
    }
  }

  function updatePeeks() {
    var before = wrapIndex(index - 1);
    var after = wrapIndex(index + 1);
    paintPeek(peekPrev, before >= 0 && before !== index ? stories[before] : null);
    paintPeek(peekNext, after >= 0 && after !== index ? stories[after] : null);
  }

  // ---- Progress rendering ----
  function buildSegments(n) {
    progressEl.innerHTML = '';
    segFills = [];
    for (var i = 0; i < n; i++) {
      var seg = document.createElement('div');
      seg.className = 'stg-seg';
      var fill = document.createElement('span');
      fill.className = 'stg-seg__fill';
      seg.appendChild(fill);
      progressEl.appendChild(seg);
      segFills.push(fill);
    }
  }

  function paintProgress(ratio) {
    for (var i = 0; i < segFills.length; i++) {
      var w = i < index ? 100 : i > index ? 0 : Math.max(0, Math.min(1, ratio)) * 100;
      segFills[i].style.width = w + '%';
    }
  }

  // ---- Playback loop ----
  function currentDurationMs() {
    var s = stories[index];
    if (s.type === 'video') {
      var v = layers[activeLayer].media;
      if (v && v.duration && isFinite(v.duration)) return v.duration * 1000;
      return (s.duration || 5) * 1000;
    }
    return (s.duration || 5) * 1000;
  }

  function tick(ts) {
    if (paused) return;
    var s = stories[index];
    var ratio;
    if (s.type === 'video') {
      var v = layers[activeLayer].media;
      if (v && v.duration && isFinite(v.duration)) {
        ratio = v.currentTime / v.duration;
      } else {
        ratio = 0;
      }
      paintProgress(ratio);
      // 'ended' event drives advance for video; keep the loop alive for the bar.
      rafId = requestAnimationFrame(tick);
      return;
    }
    var total = currentDurationMs();
    var cur = elapsed + (ts - startTs);
    ratio = cur / total;
    paintProgress(ratio);
    if (cur >= total) {
      next();
      return;
    }
    rafId = requestAnimationFrame(tick);
  }

  function startTimer() {
    cancelAnimationFrame(rafId);
    startTs = performance.now();
    rafId = requestAnimationFrame(tick);
  }

  function pause() {
    if (paused) return;
    paused = true;
    var s = stories[index];
    if (s.type === 'video') {
      var v = layers[activeLayer].media;
      if (v) v.pause();
    } else {
      elapsed += performance.now() - startTs;
    }
    cancelAnimationFrame(rafId);
  }

  function resume() {
    if (!paused) return;
    paused = false;
    var s = stories[index];
    if (s.type === 'video') {
      var v = layers[activeLayer].media;
      if (v) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
    }
    startTs = performance.now();
    rafId = requestAnimationFrame(tick);
  }

  // ---- Show a story ----
  // Callers opt into looping (open's `loop` option): past the last story comes the
  // first again, and back from the first comes the last. Without it the playlist
  // runs once — going back from the first story stays put, and running off the end
  // closes the viewer. A single story has nowhere to wrap to either way.
  // Returns -1 when there is nothing to show, which closes the viewer.
  function wrapIndex(i) {
    var n = stories.length;
    if (!n) return -1;
    if (looping && n > 1) return ((i % n) + n) % n;
    if (i < 0) return 0;
    return i >= n ? -1 : i;
  }

  function show(i) {
    i = wrapIndex(i);
    if (i < 0) {
      close();
      return;
    }
    index = i;
    elapsed = 0;
    paused = false;
    var s = stories[i];

    // Swap to the inactive layer for a cross-fade.
    var nextLayer = activeLayer === 0 ? 1 : 0;
    var layer = layers[nextLayer];

    // Clean up previous media in this layer.
    teardownLayer(layer);

    var mediaEl;
    if (s.type === 'video') {
      mediaEl = document.createElement('video');
      mediaEl.className = 'stg-media';
      mediaEl.muted = muted;
      mediaEl.playsInline = true;
      mediaEl.setAttribute('playsinline', '');
      mediaEl.setAttribute('webkit-playsinline', '');
      mediaEl.preload = 'auto';
      if (s.poster) mediaEl.poster = s.poster;
      (s.videoSources || []).forEach(function (src) {
        var source = document.createElement('source');
        source.src = src.src;
        if (src.type) source.type = src.type;
        mediaEl.appendChild(source);
      });
      mediaEl.addEventListener('ended', function () {
        next();
      });
      mediaEl.addEventListener('loadedmetadata', function () {
        // Duration now known; progress will reflect it.
      });
    } else {
      mediaEl = document.createElement('img');
      mediaEl.className = 'stg-media';
      mediaEl.alt = s.title || '';
      mediaEl.decoding = 'async';
      if (s.srcset) mediaEl.srcset = s.srcset;
      mediaEl.src = s.img;
    }

    // Replace the layer's DOM node (keeps two-layer structure intact).
    layer.el.parentNode.replaceChild(mediaEl, layer.el);
    layer.el = mediaEl;
    layer.media = mediaEl;
    layer.type = s.type;

    // Cross-fade: activate new, deactivate old.
    var oldLayer = layers[activeLayer];
    // Stop the outgoing video immediately so its audio doesn't keep playing
    // during/after the fade (the frame stays visible for the cross-fade).
    if (oldLayer.media && oldLayer.type === 'video' && oldLayer.el !== mediaEl) {
      try {
        oldLayer.media.pause();
      } catch (e) {}
    }
    requestAnimationFrame(function () {
      mediaEl.classList.add('is-active');
      if (oldLayer.el !== mediaEl) oldLayer.el.classList.remove('is-active');
    });
    activeLayer = nextLayer;

    // Header + CTA — avatar is the store logo (Instagram-style profile picture),
    // falling back to the story media if no logo is configured.
    var avatarSrc = s.avatar || s.img || s.poster || '';
    if (avatarSrc) {
      avatarEl.src = avatarSrc;
      avatarEl.style.display = '';
    } else {
      avatarEl.removeAttribute('src');
      avatarEl.style.display = 'none';
    }
    titleEl.textContent = s.title || '';
    subtitleEl.textContent = s.subtitle || '';
    if (s.ctaHref && s.ctaText) {
      ctaEl.href = s.ctaHref;
      ctaEl.textContent = s.ctaText;
      ctaEl.classList.add('is-visible');
    } else {
      ctaEl.classList.remove('is-visible');
      ctaEl.removeAttribute('href');
    }

    setOverlay(currentUid, i);
    updatePeeks();

    paintProgress(0);

    if (s.type === 'video') {
      var play = mediaEl.play();
      if (play && play.catch) {
        play.catch(function () {
          // Browser blocked autoplay with sound — fall back to muted and retry.
          if (!muted) {
            muted = true;
            mediaEl.muted = true;
            updateMuteBtn();
            var retry = mediaEl.play();
            if (retry && retry.catch) retry.catch(function () {});
          }
        });
      }
    }
    startTimer();

    preloadNext();
  }

  function teardownLayer(layer) {
    if (layer.media && layer.type === 'video') {
      try {
        layer.media.pause();
        layer.media.removeAttribute('src');
        layer.media.load();
      } catch (e) {}
    }
  }

  function preloadNext() {
    var at = wrapIndex(index + 1);
    var n = at < 0 ? null : stories[at];
    if (n && n.type === 'image' && n.img) {
      preloadImg.src = n.img;
    }
  }

  function next() {
    show(index + 1);
  }

  function prev() {
    show(index - 1);
  }

  // ---- Open / close ----
  var scrollLockY = 0;
  // Inject the bottom-of-story markup for this instance. Two flavours:
  //   [data-stg-overlay="<uid>-<index>"] — per-story markup (Stories Reels ships a
  //     full product overlay per reel); marked 'rich' so the host CSS can drop this
  //     container's own padding and gradient.
  //   [data-stg-quickadd="<uid>"]        — one shared quick-add button for the whole
  //     instance (the product page stories gallery).
  function setOverlay(sectionId, i) {
    if (!quickAddEl) return;
    var perStory = sectionId
      ? document.querySelector('template[data-stg-overlay="' + sectionId + '-' + i + '"]')
      : null;
    var tpl =
      perStory ||
      (sectionId ? document.querySelector('template[data-stg-quickadd="' + sectionId + '"]') : null);
    if (tpl && tpl.innerHTML.trim()) {
      quickAddEl.innerHTML = tpl.innerHTML;
      quickAddEl.classList.add('is-visible');
      quickAddEl.classList.toggle('stg-quickadd--rich', !!perStory);
    } else {
      quickAddEl.innerHTML = '';
      quickAddEl.classList.remove('is-visible');
      quickAddEl.classList.remove('stg-quickadd--rich');
    }
  }

  function open(list, startIndex, sectionId, opts) {
    stories = list;
    if (!stories.length) return;
    currentUid = sectionId || '';
    looping = !!(opts && opts.loop);
    buildSegments(stories.length);
    viewer.classList.add('is-open');
    document.body.classList.add('stg-playing');
    // Lock body scroll
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.overflow = 'hidden';
    show(startIndex || 0);
  }

  function close() {
    cancelAnimationFrame(rafId);
    paused = false;
    viewer.classList.remove('is-open');
    document.body.classList.remove('stg-playing');
    // Tear down both layers' video to stop playback.
    teardownLayer(layers[0]);
    teardownLayer(layers[1]);
    if (quickAddEl) {
      quickAddEl.innerHTML = '';
      quickAddEl.classList.remove('is-visible');
      quickAddEl.classList.remove('stg-quickadd--rich');
    }
    paintPeek(peekPrev, null);
    paintPeek(peekNext, null);
    document.body.style.overflow = '';
    stories = [];
    currentUid = '';
    looping = false;
  }

  // ---- Interactions ----
  function onPointerDown(e) {
    if (!viewer.classList.contains('is-open')) return;
    pointerStart = { x: e.clientX, y: e.clientY, t: performance.now() };
    moved = false;
    holdTimer = setTimeout(function () {
      pause();
    }, HOLD_MS);
  }

  function onPointerMove(e) {
    if (!pointerStart) return;
    var dy = e.clientY - pointerStart.y;
    var dx = e.clientX - pointerStart.x;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved = true;
    if (dy > SWIPE_PX && Math.abs(dy) > Math.abs(dx)) {
      clearTimeout(holdTimer);
      pointerStart = null;
      close();
    }
  }

  function onPointerUp(e) {
    if (!pointerStart) {
      resume();
      return;
    }
    clearTimeout(holdTimer);
    var heldMs = performance.now() - pointerStart.t;
    var wasPaused = paused;
    var dx = e.clientX - pointerStart.x;
    var dy = e.clientY - pointerStart.y;

    // Horizontal swipe changes story: left = forward, right = back. Checked before
    // the press-and-hold branch so a slow drag still navigates; show() clears the
    // paused state on its own.
    if (Math.abs(dx) > SWIPE_X_PX && Math.abs(dx) > Math.abs(dy)) {
      pointerStart = null;
      if (dx < 0) {
        next();
      } else {
        prev();
      }
      return;
    }

    if (wasPaused) {
      // Press-and-hold release → just resume, no navigation.
      resume();
      pointerStart = null;
      return;
    }

    if (heldMs < HOLD_MS && !moved) {
      // Short tap → navigate by zone.
      var zone = e.target.getAttribute && e.target.getAttribute('data-stg-zone');
      var rect = stage.getBoundingClientRect();
      var x = e.clientX - rect.left;
      if (zone === 'prev' || (!zone && x < rect.width / 3)) {
        prev();
      } else if (zone === 'next' || (!zone && x > (rect.width * 2) / 3)) {
        next();
      }
      // middle third = no-op
    }
    pointerStart = null;
  }

  function onKeyDown(e) {
    if (!viewer.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowRight') {
      next();
    } else if (e.key === 'ArrowLeft') {
      prev();
    }
  }

  // ---- Event delegation: open from any [data-stg-open] ----
  function onDelegatedClick(e) {
    var trigger = e.target.closest('[data-stg-open]');
    if (!trigger) return;
    e.preventDefault();
    var sectionId = trigger.getAttribute('data-stg-section');
    var startIndex = parseInt(trigger.getAttribute('data-stg-index'), 10) || 0;
    var dataEl = document.querySelector(
      'script[type="application/json"][data-stg-data="' + sectionId + '"]'
    );
    if (!dataEl) return;
    var list;
    try {
      list = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    if (!Array.isArray(list) || !list.length) return;
    open(list, startIndex, sectionId);
  }

  // ---- Thumbnail autoplay ----
  // Video thumbnails in the gallery row play muted+looping while on-screen (the
  // poster image shows until playback starts) and pause when scrolled away.
  function playThumb(v) {
    v.muted = true;
    var p = v.play();
    if (p && p.then) {
      p.then(function () {
        v.classList.add('is-playing');
      }).catch(function () {
        /* autoplay blocked — poster stays visible */
      });
    } else {
      v.classList.add('is-playing');
    }
  }

  // Play every video thumbnail muted+looping on load. (An IntersectionObserver
  // gate was tried but doesn't fire for hidden documents or thumbnails clipped by
  // the horizontally-scrolling row, so the videos never started.)
  function initThumbVideos() {
    var vids = document.querySelectorAll('video[data-stg-thumb-video]');
    for (var i = 0; i < vids.length; i++) playThumb(vids[i]);
  }

  // ---- Init ----
  function init() {
    buildViewer();
    document.addEventListener('click', onDelegatedClick);
    initThumbVideos();

    // Public handle for sections that own their click handling and so cannot use
    // the [data-stg-open] delegation (e.g. Stories Reels, whose cards carry their
    // own product links and quick-add button).
    window.StoriesViewer = {
      open: open,
      close: close,
      isOpen: function () {
        return !!viewer && viewer.classList.contains('is-open');
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
